<template>
  <AppDialogContent
    v-model="dialogValue"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between py-4">
        <div v-if="!!responseType" style="align-self: center">
          <div class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </div>
        </div>
        <div v-else style="align-self: center">
          <div class="text-h5 font-weight-bold mb-2">
            {{
              $t(
                "events:entryAction.withdraw.heading",
                "Entry withdraw confirmation"
              )
            }}
          </div>
          <p>
            {{
              $t(
                "events:entryAction.withdraw.explanation.text",
                "You are going to withdraw the following entry."
              )
            }}
          </p>
        </div>
        <div class="float-end">
          <v-btn-icon
            icon
            :disabled="formSubmitted"
            class="mx-0"
            variant="text"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="dialogValue = false"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn-icon>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :mutation-errors="mutationErrors"
        >
          <template #default>
            {{ responseTexts.main }}
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else-if="entryLoading">
        <v-progress-linear indeterminate />
      </div>
      <div v-else-if="entry">
        <AppShowEventDetails v-if="event" :event="event" show-logo />

        <br />

        <AppEntryRegistrationOverview v-bind="{ registration, competition }">
          <template #competitionCodeheader>
            {{
              $t("events:entryAction.withdraw.competitionCode.label", "Entry")
            }}
          </template>
          <template #athleteNamesHeader>
            {{
              $t("events:entryAction.withdraw.athleteNames.label", "Athlete")
            }}
          </template>
          <template #horseNamesHeader>
            {{ $t("events:entryAction.withdraw.horseNames.label", "Horse") }}
          </template>
        </AppEntryRegistrationOverview>

        <br />

        <div class="mb-2">
          <b>{{
            $t("events:entryAction.withdraw.alertNote.heading", "Please note:")
          }}</b>
        </div>

        <AppAlertNotice center>
          <template v-if="entry.connectedWithFei">
            {{
              $t(
                "events:entryAction.withdraw.alertNoteFei.text",
                "Entry will be marked as withdrawn in the FEI entry system. Please consider that there might be additional fees imposed by organising committee on late withdrawals and no show entries. For information, please check event schedule and/or FEI regulations. "
              )
            }}
          </template>
          <template v-else>
            {{
              $t(
                "events:entryAction.withdraw.alertNoteFei.text",
                "Entry will be marked as withdrawn in the FEI entry system. Please consider that there might be additional fees imposed by organising committee on late withdrawals and no show entries. For information, please check event schedule and/or FEI regulations. "
              )
            }}
          </template>
        </AppAlertNotice>
      </div>
    </template>

    <template #submit>
      {{
        $t(
          "events:entryAction.withdraw.confirmWithdrawal.button",
          "Confirm withdrawal"
        )
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row dense style="justify-content: flex-end">
        <div class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="dialogValue = false"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script setup>
import { ref, computed } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import { useEntryFeiActionsInjections } from "./compositions";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";

import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import EntryFeiResponse from "./EntryFeiResponse.vue";
import AppEntryRegistrationOverview from "@elements/AppEntryRegistrationOverview.vue";
import { injectSnackbarError } from "@/composables/injections";
import { useTranslation } from "i18next-vue";

defineOptions({
  name: "EntryFeiWithdraw"
});

const { fragments, refetchQueries } = useEntryFeiActionsInjections();
const { snackbarServerError } = injectSnackbarError();
const { t } = useTranslation();

const modal = ref(false);
const formSubmitted = ref(false);
const responseType = ref(null);
const mutationErrors = ref([]);

const entryLoading = ref(false);
const entry = ref(null);

const dialogValue = computed({
  get() {
    return modal.value;
  },
  set(v) {
    if (v == true && !modal.value) {
      responseType.value = null;
      mutationErrors.value = [];
    }
    modal.value = v;
  }
});

const registration = computed(() => {
  return entry.value?.registration;
});
const event = computed(() => {
  return entry.value?.registration?.event;
});
const competition = computed(() => {
  return entry.value?.competition;
});

const responseTexts = computed(() => {
  switch (responseType.value) {
    case "success":
      return {
        headline: t(
          "events:entryAction.withdraw.success.heading",
          "Entry withdrawal"
        ),
        main: t(
          "events:entryAction.withdraw.success.mainMessage.text",
          "Entry is withdrawn"
        ),
        additional: entry.value.connectedWithFei
          ? t(
              "events:entryAction.withdraw.success.additionalMessage.text",
              "Please note, Federation´s officials will be informed of the withdrawal. "
            )
          : "",
        okBtn: t("events:entryAction.withdraw.success.ok.button", "OK")
      };
    case "error":
      return {
        headline: t(
          "events:entryAction.withdraw.failed.heading",
          "Entry withdrawal"
        ),
        main: t(
          "events:entryAction.withdraw.failed.mainMessage.text",
          "Withdrawal failed"
        ),
        additional: t(
          "events:entryAction.withdraw.failed.additionalMessage.text",
          "Please contact your National Federation, if you need help on this matter."
        ),
        okBtn: t("events:entryAction.withdraw.failed.ok.button", "OK")
      };
    default:
      return null;
  }
});

async function openFeiActionForm({ entry: e }) {
  entryLoading.value = true;
  dialogValue.value = true;

  try {
    const { data } = await apolloClient.query({
      query: gql`
        query EntryFeiWithdrawFetch($id: ID!) {
          entry(id: $id) {
            id
            connectedWithFei
            allowedMutations
            registration {
              id
              athletes {
                id
                name
                ${AppEntryRegistrationOverview.fragments.personFields}
              }
              horses {
                id
                name
                ${AppEntryRegistrationOverview.fragments.horseFields}
              }
              eventId
              event {
                id
                ${AppShowEventDetails.fragments.event}
              }
            }
            competition {
              id
              code
              ${AppEntryRegistrationOverview.fragments.competitionFields}
            }
          }
        }
      `,
      variables: {
        id: e.id
      },
      fetchPolicy: "network-only"
    });
    entry.value = data.entry;
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    entryLoading.value = false;
  }
}

async function submit() {
  formSubmitted.value = true;

  try {
    const { errors } = await apolloClient.mutate({
      mutation: gql`
        mutation entryWithdraw($entryId: ID) {
          entryWithdraw(input: { entryId: $entryId }) {
            entry {
              id
              status
              resolution
              connectedWithFei
              registration {
                id
                status
                athleteIds
                athletes {
                  id
                }
                registrationPeople {
                  id
                  personId
                }
                horseIds
                horses {
                  id
                  isHorseSlot
                }
                registrationHorses {
                  id
                  horseId
                  isHorseSlot
                }
                allowedMutations
              }
              allowedMutations
            }
          }
        }
      `,
      variables: {
        entryId: entry.value.id
      }
    });

    if (errors?.length) {
      responseType.value = "error";
      mutationErrors.value = errors;
    } else {
      responseType.value = "success";
    }
  } catch (error) {
    responseType.value = "error";
    if (error?.graphQLErrors) {
      mutationErrors.value = error.graphQLErrors;
    }
  } finally {
    formSubmitted.value = false;
  }
}

defineExpose({ openFeiActionForm });
</script>
